import React from 'react';
import { MapPin, Percent } from 'lucide-react';

const RegionAndRatesSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  regionTaxRates 
}) => {
  // Получаем выбранный регион
  const selectedRegion = regionTaxRates.find(region => region.id === formData.region) || regionTaxRates[0];
  
  // Определяем налоговую ставку в зависимости от типа имущества и его стоимости
  const getApplicableRate = () => {
    const propertyType = formData.propertyType;
    const cadastralValue = parseFloat(formData.cadastralValue) || 0;
    
    if (propertyType === 'apartment') {
      if (cadastralValue <= 10000000) {
        return selectedRegion.rates.apartment;
      } else if (cadastralValue <= 20000000) {
        return selectedRegion.rates.apartmentHigh;
      } else if (cadastralValue <= 50000000) {
        return selectedRegion.rates.apartmentPremium;
      } else if (cadastralValue <= 300000000) {
        return selectedRegion.rates.apartmentLuxury;
      } else {
        return selectedRegion.rates.apartmentUltraLuxury;
      }
    } else if (propertyType === 'land') {
      return selectedRegion.rates.land;
    } else if (propertyType === 'office') {
      return selectedRegion.rates.office;
    } else if (propertyType === 'house' || propertyType === 'cottage') {
      return selectedRegion.rates.house;
    } else if (propertyType === 'garage') {
      return selectedRegion.rates.garage;
    } else {
      return selectedRegion.rates.other;
    }
  };

  // Получаем размер налогового вычета
  const getDeductionArea = () => {
    const propertyType = formData.propertyType;
    
    if (propertyType === 'apartment') {
      return selectedRegion.deductions.apartment || 20;
    } else if (propertyType === 'room') {
      return selectedRegion.deductions.room || 10;
    } else if (propertyType === 'house') {
      return selectedRegion.deductions.house || 50;
    } else if (propertyType === 'cottage') {
      return selectedRegion.deductions.cottage || 50;
    }
    
    return 0;
  };

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-indigo-500" />
        Регион и налоговые ставки
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Выбор региона */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Регион
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.region ? 'input-error' : ''}`}
          >
            {regionTaxRates.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.region}
            </p>
          )}
        </div>

        {/* Информация о текущей ставке */}
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-2 text-indigo-500" />
            Налоговая ставка
          </div>
          <div className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Применяемая ставка:</span>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{getApplicableRate()}%</span>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Ставка зависит от региона, типа и стоимости имущества
          </p>
        </div>
      </div>

      {/* Информация о налоговых вычетах */}
      {formData.useDeduction && ['apartment', 'room', 'house', 'cottage'].includes(formData.propertyType) && (
        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                Налоговый вычет
              </h4>
              <p className="text-xs text-green-600 dark:text-green-400">
                Для этого типа имущества предусмотрен налоговый вычет в размере {getDeductionArea()} кв.м.
              </p>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-green-700 dark:text-green-300">
                {getDeductionArea()} м²
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Таблица налоговых ставок для региона */}
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Ставки налога в регионе "{selectedRegion.name}"
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Тип имущества</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300">Ставка</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Примечание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr className={formData.propertyType === 'apartment' ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Квартира</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.apartment}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">До 10 млн ₽</td>
              </tr>
              <tr className={formData.propertyType === 'apartment' && parseFloat(formData.cadastralValue) > 10000000 && parseFloat(formData.cadastralValue) <= 20000000 ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Квартира (премиум)</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.apartmentHigh}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">10-20 млн ₽</td>
              </tr>
              <tr className={formData.propertyType === 'apartment' && parseFloat(formData.cadastralValue) > 20000000 && parseFloat(formData.cadastralValue) <= 50000000 ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Квартира (люкс)</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.apartmentPremium}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">20-50 млн ₽</td>
              </tr>
              <tr className={formData.propertyType === 'house' || formData.propertyType === 'cottage' ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Жилой дом</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.house}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400"></td>
              </tr>
              <tr className={formData.propertyType === 'office' ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Коммерческая недвижимость</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.office}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">Торговые и офисные помещения</td>
              </tr>
              <tr className={formData.propertyType === 'garage' ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Гараж/машино-место</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.garage}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400"></td>
              </tr>
              <tr className={formData.propertyType === 'land' ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">Земельный участок</td>
                <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200">{selectedRegion.rates.land}%</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">Зависит от категории земли</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RegionAndRatesSection;